import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminBlogDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };
  const { posts, loading } = useContext(BlogContext);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalViews: 0,
    totalContacts: 0,
    recentPosts: []
  });

  // Fetch contacts and calculate stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch contacts
        const contactsSnapshot = await getDocs(collection(db, 'contacts'));
        const contactsList = contactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Calculate stats
        const totalViews = posts.reduce((sum, post) => sum + (Number(post.views) || 0), 0);
        const recentPosts = posts
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 10); // Show more recent posts

        // Filter out spam and responded messages for unread count
        const unreadContacts = contactsList.filter(contact =>
          contact.status !== 'spam' && contact.status !== 'responded'
        );

        setStats({
          totalPosts: posts.length,
          totalViews,
          totalContacts: unreadContacts.length,
          recentPosts
        });
      } catch (error) {
        console.error('Error fetching blog dashboard data:', error);
      }
    };

    if (!loading) {
      fetchData();
    }
  }, [posts, loading]);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Blog Dashboard</h1>
        <p className="text-gray-400">Manage your blog posts and content</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Total Posts</p>
              <p className="text-2xl font-bold">{stats.totalPosts}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Total Views</p>
              <p className="text-2xl font-bold">{stats.totalViews}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Unread Messages</p>
              <p className="text-2xl font-bold">{stats.totalContacts}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Avg. Views/Post</p>
              <p className="text-2xl font-bold">
                {stats.totalPosts > 0 ? Math.round(stats.totalViews / stats.totalPosts) : 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/admin/blog"
              className="flex items-center p-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg transition-colors"
            >
              <div className="p-2 bg-blue-600 rounded mr-3">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium">Create New Blog Post</p>
                <p className="text-sm text-gray-400">Write and publish a new article</p>
              </div>
            </Link>

            <Link
              to="/admin/blog-stats"
              className="flex items-center p-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg transition-colors"
            >
              <div className="p-2 bg-green-600 rounded mr-3">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="font-medium">View Blog Statistics</p>
                <p className="text-sm text-gray-400">Analyze post performance and views</p>
              </div>
            </Link>

            <Link
              to="/admin/contacts"
              className="flex items-center p-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg transition-colors"
            >
              <div className="p-2 bg-purple-600 rounded mr-3">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium">View Contact Messages</p>
                <p className="text-sm text-gray-400">Review and respond to inquiries</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Posts */}
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Recent Posts</h2>
            <Link
              to="/admin/blog-stats"
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              View All →
            </Link>
          </div>
          {stats.recentPosts.length > 0 ? (
            <div className="space-y-3">
              {stats.recentPosts.slice(0, 5).map(post => (
                <div key={post.id} className="flex justify-between items-center p-3 bg-[#2a2a2a] rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium truncate">{post.title}</p>
                    <p className="text-sm text-gray-400">{post.views || 0} views</p>
                  </div>
                  <Link
                    to={`/blog/${post.id}`}
                    className="text-blue-500 hover:text-blue-400 text-sm ml-3"
                    target="_blank"
                  >
                    View
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No posts yet</p>
          )}
        </div>
      </div>

      {/* Performance Overview */}
      <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Performance Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-400 mb-2">
              {stats.totalViews > 0 ? Math.round((stats.totalViews / Math.max(stats.totalPosts, 1)) * 1.2) : 0}
            </div>
            <div className="text-sm text-gray-400">Estimated Monthly Views</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-2">
              {stats.totalContacts}
            </div>
            <div className="text-sm text-gray-400">Active Conversations</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">
              {stats.totalPosts > 0 ? Math.round(stats.totalPosts / 12) : 0}
            </div>
            <div className="text-sm text-gray-400">Posts per Month</div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogDashboard;
